import { useEffect } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { PublicFooter, PublicHeader } from '@/components/AppShell'
import {
  LEARN_ARTICLE_CLASS,
  LEARN_CONTAINER_CLASS,
  findLearnArticle,
} from '@/lib/learn'

/**
 * Long-form "Learn" article page at /learn/{slug}.
 *
 * The body is hand-authored, build-time HTML (see src/lib/learn.ts) and
 * is injected verbatim — the same string the prerender plugin writes into
 * the static HTML — so crawlers and the React render paint identically.
 * Per-page <title>/<meta>/canonical/Article JSON-LD live in the
 * prerendered head; on client-side nav only the tab title updates here
 * (commons has no react-helmet), matching IndPage's approach.
 *
 * The article markup here MUST stay in lockstep with renderLearnShell()
 * in scripts/inject-catalog-plugin.ts — both wrap the shared bodyHtml in
 * the same container + prose classes, so the only thing to keep aligned
 * is the wrapper chrome (already shared via the class constants).
 */
export function LearnPage() {
  const { slug } = useParams<{ slug: string }>()
  const article = findLearnArticle(slug)

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | CTDCommons`
    }
  }, [article])

  // Unknown slug. On a direct load this branch never runs — vercel.json
  // rewrites /learn/:slug to a prerendered file; a missing one 404s at the
  // edge. This only fires on client-side nav from a stale link.
  if (!article) return <Navigate to="/" replace />

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <main className="flex-1">
        <div className={LEARN_CONTAINER_CLASS}>
          <nav className="text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-brand-orange transition-colors">
              CTDCommons
            </Link>
            <span className="mx-1.5 text-slate-300">/</span>
            <span className="text-slate-600">{article.navTitle}</span>
          </nav>

          <header className="mb-8 space-y-3">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-charcoal leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-slate-500 leading-relaxed">
              {article.dek}
            </p>
          </header>

          {/* bodyHtml is trusted, build-time, hand-authored markup committed
              to the repo (src/lib/learn.ts) — no user input reaches it, so
              dangerouslySetInnerHTML carries no XSS surface here. It is the
              same string the prerender writes, keeping hydration in step. */}
          <article
            className={LEARN_ARTICLE_CLASS}
            dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
          />
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}
